import { reportLeak } from '@/lib/telemetry';

/**
 * Failure codes, in plain language.
 *
 * The contract is explicit: the **code** is the contract and the message is
 * presentation. The server does send a client-safe `message` on every
 * `error_code` frame, but we render our own wording keyed on the code, for two
 * reasons.
 *
 * First, consistency: the same failure should read the same way whether it
 * arrives on the stream, in a 409 body or on a stale chat fetch. Second,
 * containment: the day a scrub rule misses something, the thing on screen is a
 * string from this file rather than a string from the run.
 *
 * `usage_limit_reached` is the one the spec calls out. There is no quota meter
 * in this product - the user removed it - and no vendor is ever named. It is a
 * neutral wait, phrased as one.
 */

export interface ErrorCopy {
  title: string;
  body: string;
  /** Whether asking again is a sensible next move. */
  retryable: boolean;
}

const ERRORS: Record<string, ErrorCopy> = {
  agent_failed: {
    title: 'The work stopped before it finished',
    body: 'Nothing was published and nothing was lost. Try describing the change again, in a bit more detail if you can.',
    retryable: true,
  },
  run_expired: {
    title: 'This one took too long and was stopped',
    body: 'That is on us, not on what you asked for. Send it again and it will start fresh.',
    retryable: true,
  },
  usage_limit_reached: {
    title: 'Usage limit reached',
    body: 'This workspace has reached its limit for now. It clears on its own. Get in touch if you need it sooner.',
    retryable: false,
  },
  credential_unavailable: {
    title: 'This workspace needs reconnecting',
    body: 'The connection behind this workspace is not available right now. Your account manager has been notified.',
    retryable: false,
  },
  context_exhausted: {
    title: 'This conversation is full',
    body: 'It has grown too long to add to. Everything already asked for is safe and still on your preview. Get in touch and we will carry on in a fresh conversation.',
    retryable: false,
  },
  deploy_failed: {
    title: 'The preview site did not update',
    body: 'The change was made but it could not be published to your preview. Ask again and we will retry it.',
    retryable: true,
  },
  deploy_unverified: {
    title: 'The preview did not come back up',
    body: 'The change was published but the site did not answer afterwards, so we are not showing it as ready. Ask again and we will retry.',
    retryable: true,
  },
  push_rejected: {
    title: 'The change could not be saved',
    body: 'Something changed underneath this work while it was running. Ask again and it will pick up from where things now stand.',
    retryable: true,
  },
  content_scan_blocked: {
    title: 'The change was held back by a safety check',
    body: 'A routine check found something in the change that should not be published, so nothing went out. Try describing what you want a different way, or get in touch.',
    retryable: true,
  },
  agent_config_unverified: {
    title: 'The work could not start safely',
    body: 'A check we run before every change did not pass, so nothing was started and nothing was altered. We have been told about it — try again shortly.',
    retryable: true,
  },
  agent_unavailable: {
    title: 'The workspace could not be reached',
    body: 'Nothing was started and nothing was changed. This is usually brief — try again in a moment.',
    retryable: true,
  },
  run_setup_failed: {
    title: 'We could not open a copy of your product',
    body: 'Nothing was changed. Try asking again, and get in touch if it keeps happening.',
    retryable: true,
  },
  run_gate_failed: {
    title: 'This request could not be started',
    body: 'Something about this workspace stopped it before it began. Nothing was changed — get in touch and we will sort it out.',
    retryable: false,
  },
  repo_unavailable: {
    title: 'Part of your product could not be opened',
    body: 'Nothing was changed. We have been told about it — try again shortly.',
    retryable: true,
  },
  remote_unavailable: {
    title: 'Your product could not be reached where it is stored',
    body: 'Nothing was published and nothing was lost. Try again in a moment.',
    retryable: true,
  },
  commit_failed: {
    title: 'The work could not be written down',
    body: 'Nothing was published. Ask again and it will start from where things now stand.',
    retryable: true,
  },
  rate_limited: {
    title: 'Too many requests in a row',
    body: 'Give it a moment and try again.',
    retryable: true,
  },
  session_expired: {
    title: 'Your session ended',
    body: 'Sign in again to carry on. Nothing has been lost.',
    retryable: false,
  },
};

const UNKNOWN_ERROR: ErrorCopy = {
  title: 'Something went wrong',
  body: 'Nothing you asked for has been lost. Try again in a moment, and get in touch if it keeps happening.',
  retryable: true,
};

export function errorCopy(code: string | null | undefined): ErrorCopy {
  if (!code) return UNKNOWN_ERROR;
  const found = ERRORS[code];
  if (found) return found;
  reportLeak({ kind: 'unknown_error_code', token: String(code).slice(0, 48) });
  return UNKNOWN_ERROR;
}

export const ALL_ERROR_CODES = Object.keys(ERRORS);
